import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import * as api from '../../api/index'
import { useRhGuard } from '../../hooks/useRoleGuard'
import { isAdmin } from '../../utils/auth'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import Spinner from '../Spinner/Spinner'
import { formatTND } from '../../utils/formatTND'
import styles from './Coaches.module.css'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const Coaches = () => {
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useRhGuard()
  const admin = isAdmin()
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [selectedIds, setSelectedIds] = useState([])
  const [deleting, setDeleting] = useState(false)
  const [confirmModal, setConfirmModal] = useState(null)

  const loadCoaches = useCallback(async () => {
    setLoading(true)
    try {
      const { data: result } = await api.fetchCoaches({ search, page, limit: pageSize })
      setData(result)
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur chargement coachs')
    } finally {
      setLoading(false)
    }
  }, [search, page, pageSize])

  useEffect(() => {
    loadCoaches()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, pageSize])

  useEffect(() => {
    setPage(1)
  }, [search, pageSize])

  useEffect(() => {
    setSelectedIds([])
  }, [page, search, pageSize])

  const handleDelete = async () => {
    if (!confirmModal?.ids?.length) return
    const ids = confirmModal.ids
    setDeleting(true)
    try {
      if (ids.length === 1) {
        await api.deleteCoach(ids[0])
      } else {
        await api.deleteCoachesBulk(ids)
      }
      openSnackbarRef.current(`${ids.length} coach(s) supprimé(s)`)
      setConfirmModal(null)
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)))
      const remainingOnPage = (data?.coaches?.length ?? ids.length) - ids.length
      if (remainingOnPage === 0 && page > 1) {
        setPage((p) => p - 1)
      } else {
        loadCoaches()
      }
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur suppression')
    } finally {
      setDeleting(false)
    }
  }

  const requestDeleteCoaches = (ids) => {
    if (!ids.length) return
    setConfirmModal({ ids })
  }

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  if (loading && !data) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  const { coaches = [], pagination, stats } = data || {}
  const totalPages = pagination?.totalPages || 1
  const totalCoaches = pagination?.total || 0
  const showPagination = coaches.length > 0
  const pageIds = coaches.map((c) => c._id)
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id))

  const toggleSelectAllOnPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)))
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])])
    }
  }

  const deleteCount = confirmModal?.ids?.length ?? 0
  const selectedCoaches = coaches.filter((c) => confirmModal?.ids?.includes(c._id))
  const totalInvoicesInSelection = selectedCoaches.reduce(
    (sum, c) => sum + (c.invoiceCount || 0),
    0
  )

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div>
          <h1>Référentiel coaches</h1>
          <p>Historique et fiches coaches — synchronisées à chaque import</p>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>Total coaches</span>
          <span className={styles.kpiValue}>{stats?.totalCoaches || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Factures émises</span>
          <span className={styles.kpiValue}>{stats?.totalInvoices || 0}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Total honoraires</span>
          <span className={styles.kpiValue}>{formatTND(stats?.totalHonoraires)} TND</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Net cumulé</span>
          <span className={styles.kpiValue}>{formatTND(stats?.totalNet)} TND</span>
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label htmlFor="coach-search">Rechercher</label>
          <input
            id="coach-search"
            type="search"
            className={styles.searchInput}
            placeholder="Nom, MF, email, RIB, ID Sage..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <div>
            <h2>Liste des coaches</h2>
            <p className={styles.hint}>{pagination?.total || 0} coach(s) enregistré(s)</p>
          </div>
          {admin && selectedIds.length > 0 && (
            <div className={styles.panelActions}>
              <button
                type="button"
                className={styles.deleteBulkBtn}
                disabled={deleting || loading}
                onClick={() => requestDeleteCoaches(selectedIds)}
              >
                Supprimer ({selectedIds.length})
              </button>
            </div>
          )}
        </div>

        {coaches.length === 0 ? (
          <p className={styles.empty}>
            Aucun coach enregistré. Importez un fichier Excel pour créer les fiches automatiquement.
          </p>
        ) : (
          <>
            <div className={`${styles.tableWrap} table-scroll-wrap`}>
              <table className={`${styles.table} responsive-table`}>
                <thead>
                  <tr>
                    {admin && (
                      <th className={styles.checkCol}>
                        <input
                          type="checkbox"
                          aria-label="Tout sélectionner sur cette page"
                          checked={allPageSelected}
                          onChange={toggleSelectAllOnPage}
                        />
                      </th>
                    )}
                    <th>Coach</th>
                    <th>Email</th>
                    <th>RIB</th>
                    <th>Factures</th>
                    <th>Honoraires cumulés</th>
                    <th>Dernière campagne</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {coaches.map((coach) => (
                    <tr key={coach._id}>
                      {admin && (
                        <td className={styles.checkCol} data-label="Sélection">
                          <input
                            type="checkbox"
                            aria-label={`Sélectionner ${coach.name}`}
                            checked={selectedIds.includes(coach._id)}
                            onChange={() => toggleSelect(coach._id)}
                          />
                        </td>
                      )}
                      <td data-label="Coach">
                        <div className={styles.coachCell}>
                          <div className={styles.avatar}>
                            {coach.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <span className={styles.coachName}>{coach.name}</span>
                            <span className={styles.coachMf}>{coach.matriculeFiscal}</span>
                          </div>
                        </div>
                      </td>
                      <td data-label="Email">{coach.email || '—'}</td>
                      <td data-label="RIB">{coach.rib || '—'}</td>
                      <td data-label="Factures">{coach.invoiceCount || 0}</td>
                      <td data-label="Honoraires cumulés">{formatTND(coach.totalHonoraires)} TND</td>
                      <td data-label="Dernière campagne">{coach.periodLabel || '—'}</td>
                      <td data-label="Actions">
                        <div className={`${styles.actionCell} action-cell-inner`}>
                          <button
                            type="button"
                            className={styles.tableBtn}
                            onClick={() => history.push(`/coaches/${coach._id}`)}
                          >
                            Voir fiche
                          </button>
                          {admin && (
                            <button
                              type="button"
                              className={styles.deleteBtn}
                              disabled={deleting}
                              onClick={() => requestDeleteCoaches([coach._id])}
                            >
                              Supprimer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {showPagination && (
              <div className={styles.pagination}>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Précédent
                </button>
                <span className={styles.pageLabel}>
                  Page {pagination?.page || page} sur {totalPages}
                  {' · '}{totalCoaches} coach(s)
                </span>
                <button
                  type="button"
                  className={styles.pageBtn}
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant →
                </button>
                <select
                  className={styles.pageSizeSelect}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  aria-label="Nombre de coaches par page"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>{size} / page</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmModal
        open={Boolean(confirmModal)}
        variant="warning"
        title={deleteCount > 1 ? `Supprimer ${deleteCount} coaches ?` : 'Supprimer ce coach ?'}
        message={
          deleteCount > 1
            ? `Vous allez supprimer ${deleteCount} fiches coach du référentiel.`
            : 'Vous allez supprimer cette fiche coach du référentiel.'
        }
        detail={
          totalInvoicesInSelection > 0
            ? `Les ${totalInvoicesInSelection} facture(s) associée(s) restent dans leurs campagnes. Un prochain import peut recréer la fiche coach.`
            : 'Un prochain import Excel peut recréer automatiquement la fiche coach.'
        }
        confirmLabel={deleting ? 'Suppression…' : 'Supprimer'}
        cancelLabel="Annuler"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => !deleting && setConfirmModal(null)}
      />
    </div>
  )
}

export default Coaches
